// Shared quantity-stepper behavior for any Alpine component that exposes
// `handle`, `variantId`, and `quantity` (gridProduct, scienceProduct). Used by
// snippets/quantity-pill.liquid's +/- buttons. Keeps each card's own quantity
// in sync with the shared `$store.promo` pending pool, so every product card
// tied to the same promo (science-hub showcase grid, collection grid) shows
// one combined tier-progress message instead of only knowing about itself.
function quantityPendingMixin() {
  return {
    incrementQty() {
      const max = Alpine.store('inventorySync').getTrueAvailable(this.variantId);
      if (this.quantity < max) {
        this.quantity++;
        this.syncPendingPromo();
      }
    },
    decrementQty() {
      if (this.quantity > 1) {
        this.quantity--;
        this.syncPendingPromo();
      }
    },
    syncPendingPromo() {
      const promo = Alpine.store('promo');
      if (!promo || !this.variantId || !promo.isProductApplicable(this.handle)) return;
      promo.setPending(this.handle, this.quantity);
    },
    clearPendingPromo() {
      const promo = Alpine.store('promo');
      if (promo) promo.clearPending(this.handle);
    }
  };
}

document.addEventListener('alpine:init', () => {
  // Initialize the runtime persona switcher interaction flag
  window.zatechPersonaInteracted = false;

  // --- GLOBAL ORCHESTRATOR (Modal & Scroll Management) ---
  if (!Alpine.store('orchestrator')) {
    Alpine.store('orchestrator', {
      activeModal: null,
      previousModal: null,
      zoomImage: null,
      lockBody() { 
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden'; 
        document.documentElement.classList.add('modal-locked');
        document.body.classList.add('modal-locked');
      },
      unlockBody() { 
        document.documentElement.style.overflow = '';
        document.body.style.overflow = ''; 
        document.documentElement.classList.remove('modal-locked');
        document.body.classList.remove('modal-locked');
      },
      open(id) {
        if (this.activeModal && this.activeModal !== id) {
          if (this.activeModal !== 'product_splash' && id !== 'zoom_splash') {
            this.close();
          }
        }
        this.activeModal = id;
        this.lockBody();
        window.dispatchEvent(new CustomEvent('zatech:modal-open', { detail: { id } }));
      },
      openZoom(imageUrl) {
        this.previousModal = this.activeModal;
        this.zoomImage = imageUrl.replace(/&width=\d+/, '&width=2400');
        this.open('zoom_splash');
      },
      close() {
        if (this.activeModal === 'zoom_splash' && this.previousModal) {
          this.activeModal = this.previousModal;
          this.previousModal = null;
        } else {
          this.activeModal = null;
          this.previousModal = null;
          this.unlockBody();
        }
      },
      init() {
        window.addEventListener('open-product-splash', (e) => {
          this.open('product_splash');
        });
        window.addEventListener('close-product-splash', () => {
          if (this.activeModal === 'product_splash') {
            this.close();
          }
        });
      }
    });
  }

  // --- TELEMETRY BUS (Analytics Layer) ---
  if (!Alpine.store('telemetry')) {
    Alpine.store('telemetry', {
      track(event, data = {}) {
        const isDebug = sessionStorage.getItem('zatech_debug') === 'true' || window.Shopify?.designMode;
        if (isDebug) {
          console.log(`[Telemetry] ${event}`, data);
        }
        window.dispatchEvent(new CustomEvent(`zatech:telemetry`, { 
          detail: { event, data, timestamp: Date.now() } 
        }));
      }
    });
  }

  // --- PERSONA STATE ---
  if (!Alpine.store('persona')) {
    let initialPersona = localStorage.getItem('zatech_persona') || 'relief';
    if (initialPersona === 'pain' || initialPersona === 'relief') initialPersona = 'relief';
    else if (initialPersona === 'active' || initialPersona === 'performance') initialPersona = 'performance';
    else if (initialPersona === 'standing' || initialPersona === 'support') initialPersona = 'support';

    Alpine.store('persona', {
      current: initialPersona,
      set(val) {
        let normalized = val;
        if (val === 'pain') normalized = 'relief';
        else if (val === 'active') normalized = 'performance';
        else if (val === 'standing') normalized = 'support';

        this.current = normalized;
        localStorage.setItem('zatech_persona', normalized);
        window.zatechPersonaInteracted = true; // Explicit user interaction recorded
        Alpine.store('telemetry').track('persona_toggle', { persona: normalized });
      }
    });
  }

  // --- INVENTORY SYNC (Single Source of Truth) ---
  if (!Alpine.store('inventorySync')) {
    Alpine.store('inventorySync', {
      warehouseStock: {},
      
      init() {
        // Phase 1: Hydrate from all Liquid-injected Registries
        document.querySelectorAll('[data-inventory-registry]').forEach(seed => {
          try {
            const data = JSON.parse(seed.textContent);
            Object.assign(this.warehouseStock, data);
          } catch (e) { console.error('[InventorySync] Registry hydration failed', e); }
        });


        // Phase 2: Trigger Sync after idle
        setTimeout(() => this.backgroundSync(), 1500);

        // Phase 3: Trigger Sync on Drawer Open or Window Focus
        window.addEventListener('zatech:modal-open', (e) => {
          if (e.detail && e.detail.id === 'cart_splash') {
            this.backgroundSync();
          }
        });
        window.addEventListener('focus', () => {
          this.backgroundSync();
        });
      },

      getTrueAvailable(variantId) {
        if (!variantId) return 0;
        const warehouse = this.warehouseStock[variantId] !== undefined ? Number(this.warehouseStock[variantId]) : 999;
        const inCart = Alpine.store('cartModule').items
          .filter(item => Number(item.variant_id) === Number(variantId))
          .reduce((sum, item) => sum + Number(item.quantity), 0);
        
        return Math.max(0, warehouse - inCart);
      },

      isSoldOut(variantId) {
        if (!variantId) return false;
        return this.getTrueAvailable(variantId) <= 0;
      },

      getAvailabilityMessage(variantId, totalCommitment) {
        if (!variantId) return '';
        const warehouse = Number(this.warehouseStock[variantId] || 0);
        
        // Pure comparison: Is the proposed total at/above the warehouse ceiling?
        if (warehouse > 0 && Number(totalCommitment || 0) >= warehouse) {
          return `Only ${warehouse} available`;
        }
        return '';
      },

      calculateTotalCommitment(variantId, proposedQty = 0) {
        if (!variantId) return 0;
        const inCart = Alpine.store('cartModule').items
          .filter(item => Number(item.variant_id) === Number(variantId))
          .reduce((sum, item) => sum + Number(item.quantity), 0);
        return Number(proposedQty) + inCart;
      },

      async backgroundSync() {
        const productElements = document.querySelectorAll('[data-product-handle]');
        const handlesFromDOM = Array.from(productElements).map(el => el.dataset.productHandle);
        const handlesFromCart = Alpine.store('cartModule').items.map(item => item.handle);
        const uniqueHandles = [...new Set([...handlesFromDOM, ...handlesFromCart])];
        
        if (uniqueHandles.length === 0) return;

        try {
          const syncPromises = uniqueHandles.map(async (handle) => {
            const response = await fetch(`/products/${handle}?view=inventory`);
            if (response.ok) {
              const data = await response.json();
              data.variants.forEach(v => {
                this.warehouseStock[v.id] = v.inventory;
              });
            }
          });
          await Promise.all(syncPromises);
          window.dispatchEvent(new CustomEvent('inventory-synced'));
        } catch (e) { console.error('[InventorySync] Sync failed', e); }
      }
    });
  }

  // --- CART MODULE (Authority) ---
  if (!Alpine.store('cartModule')) {
    Alpine.store('cartModule', {
      count: 0,
      active: false,
      loading: false,
      items: [],
      total: 0,
      get originalTotal() {
        return (this.items || []).reduce((sum, item) => {
          return sum + (item.original_line_price !== undefined ? item.original_line_price : item.final_line_price);
        }, 0);
      },
      pendingUpdates: {},
      updateTimer: null,

      init() {
        const seed = document.getElementById('cart-seed');
        if (seed) {
          try {
            const data = JSON.parse(seed.textContent);
            this.count = data.item_count;
            this.total = data.total_price;
            this.items = (data.items || []).map(item => {
              const originalPrice = item.original_line_price !== undefined ? item.original_line_price : item.final_line_price;
              item.original_unit_price = Math.round(originalPrice / item.quantity);
              item.final_unit_price = Math.round(item.final_line_price / item.quantity);

              // Normalize image URLs to prevent re-render flickering
              if (item.image) {
                let img = item.image;
                if (img.indexOf('//') === 0) img = 'https:' + img;
                let cleanImg = img.split('?')[0].replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|1024x1024|2048x2048|master|\d+x\d+)(?:_crop_[a-z]+)?(?=\.(?:jpg|jpeg|gif|png|webp|avif))/i, '');
                item.thumbnail = cleanImg + '?width=112';
                item.cartThumbnail = cleanImg + '?width=160';
              } else {
                item.thumbnail = '';
                item.cartThumbnail = '';
              }
              
              return item;
            });
          } catch (e) { console.error('[CartModule] Seed hydration failed', e); }
        }
      },

      _hydrateFromServer(cart) {
        // Overlay any un-synced optimistic clicks onto the server response
        cart.items.forEach(item => {
          const originalPrice = item.original_line_price !== undefined ? item.original_line_price : item.final_line_price;
          item.original_unit_price = Math.round(originalPrice / item.quantity);
          // Real per-unit price Shopify actually charges at this item's server-confirmed
          // quantity, including any cart-level volume discount. Must be captured before
          // quantity is overwritten below with the client-authoritative value, and used
          // (not original_unit_price) to recompute final_line_price after — otherwise every
          // hydration silently discards the real discount and final_line_price collapses
          // back to full price.
          item.final_unit_price = Math.round(item.final_line_price / item.quantity);

          // Normalize image URLs to prevent re-render flickering
          if (item.image) {
            let img = item.image;
            if (img.indexOf('//') === 0) img = 'https:' + img;
            let cleanImg = img.split('?')[0].replace(/_(?:pico|icon|thumb|small|compact|medium|large|grande|1024x1024|2048x2048|master|\d+x\d+)(?:_crop_[a-z]+)?(?=\.(?:jpg|jpeg|gif|png|webp|avif))/i, '');
            item.thumbnail = cleanImg + '?width=112';
            item.cartThumbnail = cleanImg + '?width=160';
          } else {
            item.thumbnail = '';
            item.cartThumbnail = '';
          }

          // Use client state as the absolute authority for quantities during the active session
          const localItem = this.items.find(i => i.key === item.key || Number(i.variant_id) === Number(item.variant_id));
          if (this.pendingUpdates[item.key] !== undefined) {
            item.quantity = this.pendingUpdates[item.key];
          } else if (localItem) {
            item.quantity = localItem.quantity;
          }
          
          if (item.original_line_price !== undefined) {
            item.original_line_price = item.original_unit_price * item.quantity;
          }
          item.final_line_price = item.final_unit_price * item.quantity;
        });
        
        // Ensure optimistically removed items aren't rendered, and filter out zero-quantity items
        const visibleItems = cart.items.filter(item => {
          const pending = this.pendingUpdates[item.key];
          const hasValidQty = Number(item.quantity) > 0;
          const isNotOptimisticallyRemoved = pending === undefined || Number(pending) !== 0;
          return hasValidQty && isNotOptimisticallyRemoved;
        });

        this.count = visibleItems.reduce((sum, i) => sum + i.quantity, 0);
        this.items = visibleItems;
        this.total = visibleItems.reduce((sum, i) => sum + i.final_line_price, 0);
      },

      async add(variantId, qty = 1, skipRefresh = false, source = 'unknown') {
        if (this.loading) return false;
        
        const trueAvailable = Alpine.store('inventorySync').getTrueAvailable(variantId);
        if (trueAvailable < qty) {
          console.warn('[Cart] Blocked: Exceeds True Availability');
          return false;
        }

        // Save old state for fallback rollback
        const oldItems = JSON.parse(JSON.stringify(this.items));
        const oldCount = this.count;
        const oldTotal = this.total;

        // Optimistic update if item already exists in cart
        const existingItem = this.items.find(item => Number(item.variant_id) === Number(variantId));
        if (existingItem) {
          const unitPrice = existingItem.final_line_price / existingItem.quantity;
          existingItem.quantity += qty;
          existingItem.final_line_price += unitPrice * qty;
          this.count += qty;
          this.total += unitPrice * qty;
        }

        this.loading = true;
        try {
          const targetQty = existingItem ? existingItem.quantity : qty;

          // Call update.js to add/update item and retrieve full cart state in one request
          const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ updates: { [variantId]: targetQty } })
          });
          if (!response.ok) throw new Error('Cart Update API returned non-OK status');
          
          const cart = await response.json();
          this._hydrateFromServer(cart);

          // Find the added item inside the updated cart items list for telemetry
          const addedItem = this.items.find(item => Number(item.variant_id) === Number(variantId)) || {};

          // Retrieve precise metadata from Shopify's standard AJAX response
          const productTitle = addedItem.product_title || addedItem.title || 'ZaTech Product';
          const price = addedItem.price ? (addedItem.price / 100) : 35.00;
          const brand = addedItem.vendor || 'ZaTech';
          const persona = Alpine.store('persona')?.current || 'performance';
          const currency = window.Shopify?.currency?.active || 'USD';

          Alpine.store('telemetry').track('atc_success', { 
            variantId, 
            quantity: qty,
            productTitle,
            price,
            brand,
            persona,
            currency,
            source
          });

          if (!skipRefresh) {
            Alpine.store('orchestrator').open('cart_splash');
            this.active = true;
          }
        } catch (e) {
          console.error('Cart Add Failed, rolling back', e);
          // Rollback on error
          this.items = oldItems;
          this.count = oldCount;
          this.total = oldTotal;
          this.loading = false;
          return false;
        }
        this.loading = false;
        window.dispatchEvent(new CustomEvent('cart-updated'));
        return true;
      },

      async refresh(silent = false) {
        this.loading = true;
        try {
          const response = await fetch('/cart.js');
          const cart = await response.json();
          this._hydrateFromServer(cart);
          
          if (!silent) {
            Alpine.store('orchestrator').open('cart_splash');
            this.active = true; 
          }
        } catch (e) { console.error('Cart refresh failed', e); }
        this.loading = false;
        window.dispatchEvent(new CustomEvent('cart-updated'));
      },

      async updateQuantity(key, newQty) {
        const qty = parseInt(newQty, 10);
        if (isNaN(qty) || qty < 0) return;

        const item = this.items.find(i => i.key === key);
        if (item) {
          const warehouse = Alpine.store('inventorySync').warehouseStock[item.variant_id] !== undefined 
            ? Number(Alpine.store('inventorySync').warehouseStock[item.variant_id]) 
            : 999;
          if (qty > warehouse) {
            console.warn(`[Cart] Inventory limit reached: ${warehouse}`);
            return;
          }
        }

        // Optimistic UI Update (Immediate, no blocking)
        if (item) {
          if (qty === 0) {
            this.items = this.items.filter(i => i.key !== key);
          } else {
            const unitPrice = item.original_unit_price || Math.round((item.original_line_price !== undefined ? item.original_line_price : item.final_line_price) / item.quantity);
            // Preserve the item's last-known real discounted unit price through this optimistic
            // bump rather than reverting to full price — see the matching note in
            // _hydrateFromServer. This is an optimistic estimate (won't reflect a tier crossed
            // by this exact click until the debounced round-trip confirms), not a fabricated one.
            const finalUnitPrice = item.final_unit_price || Math.round(item.final_line_price / item.quantity);
            item.quantity = qty;
            if (item.original_line_price !== undefined) {
              item.original_line_price = unitPrice * qty;
            }
            item.final_line_price = finalUnitPrice * qty;
          }
          this.count = this.items.reduce((sum, i) => sum + i.quantity, 0);
          this.total = this.items.reduce((sum, i) => sum + i.final_line_price, 0);
          
          // Queue Network Update using line item key (natively supported by /cart/update.js)
          this.pendingUpdates[item.key] = qty;
        }

        clearTimeout(this.updateTimer);
        this.updateTimer = setTimeout(() => {
          this.syncPendingUpdates();
        }, 400); // Debounce duration
      },

      async syncPendingUpdates() {
        if (Object.keys(this.pendingUpdates).length === 0) return;
        
        const updatesToSync = { ...this.pendingUpdates };
        this.loading = true;

        try {
          const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ updates: updatesToSync })
          });
          
          if (response.ok) {
            const cart = await response.json();
            
            // Clean up successfully synced keys from the active queue
             Object.keys(updatesToSync).forEach(key => {
               if (this.pendingUpdates[key] === updatesToSync[key]) {
                 delete this.pendingUpdates[key];
               }
             });

             this._hydrateFromServer(cart);
          } else {
            throw new Error('Quantity update API failed');
          }
        } catch (e) { 
          console.error('[Cart] Sync failed, forcing full refresh', e); 
          await this.refresh(true); 
        }
        
        this.loading = false;
        window.dispatchEvent(new CustomEvent('cart-updated'));
      },

      closeSplash() {
        this.active = false;
        Alpine.store('orchestrator').close();
      },

      async removeItem(key) { await this.updateQuantity(key, 0); },

      formatMoney(cents) {
        if (typeof cents === 'string') cents = cents.replace('.', '');
        let value = '';
        let placeholderRegExp = /\{\{\s*(\w+)\s*\}\}/;
        let formatString = '{{ amount_no_decimals }}'; // Default fallback
        
        // Try to get shop format or use default
        try { formatString = document.body.dataset.moneyFormat || '${{amount}}'; } catch(e) {}

        function formatWithDelimiters(number, precision, thousands, decimal) {
          precision = precision || 2;
          thousands = thousands || ',';
          decimal = decimal || '.';
          if (isNaN(number) || number == null) return 0;
          number = (number / 100.0).toFixed(precision);
          let parts = number.split('.'),
              dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
              cents = parts[1] ? (decimal + parts[1]) : '';
          return dollars + cents;
        }

        switch (formatString.match(placeholderRegExp)[1]) {
          case 'amount': value = formatWithDelimiters(cents, 2); break;
          case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
          case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
          case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
        }
        return formatString.replace(placeholderRegExp, value);
      },

      quickCheckout(source = 'unknown') {
        if (this.count === 0) return;
        
        try {
          Alpine.store('telemetry').track('begin_checkout', {
            checkoutSource: source,
            items: this.items.map(item => ({
              variantId: item.variant_id,
              productTitle: item.product_title || item.title,
              price: item.final_unit_price / 100,
              brand: item.vendor || 'ZaTech',
              quantity: item.quantity,
              persona: Alpine.store('persona')?.current || 'performance'
            })),
            totalPrice: this.total / 100,
            currency: window.Shopify?.currency?.active || 'USD'
          });
        } catch (e) {
          console.error('[Telemetry] Failed to track begin_checkout', e);
        }

        // Standard delay to ensure event dispatches before page unload
        setTimeout(() => {
          window.location.href = '/checkout';
        }, 150);
      },

      viewCart() {
        if (this.count === 0) return;
        window.location.href = '/cart';
      }
    });

    // Auto-clamp cart quantities on inventory sync
    window.addEventListener('inventory-synced', () => {
      const cart = Alpine.store('cartModule');
      if (cart && cart.items) {
        cart.items.forEach(item => {
          const warehouse = Number(Alpine.store('inventorySync').warehouseStock[item.variant_id]);
          if (!isNaN(warehouse) && item.quantity > warehouse) {
            console.warn(`[Inventory Sync] Stock dropped. Auto-clamping ${item.key} from ${item.quantity} to ${warehouse}`);
            cart.updateQuantity(item.key, warehouse);
          }
        });
      }
    });
  }

  // --- OVERFLOW FADE (spec §31.4) ---
  // x-overflow-fade on a row that scrolls sideways: .is-overflow while its content is wider
  // than the row, .is-end once scrolled to the right end (assets/zatech-mobile.css fades the
  // right edge in between). Presentation only. One ResizeObserver serves every row (the home
  // page has about 95); rows inside hidden colors are measured when they are shown.
  const overflowRows = new WeakMap();
  const updateOverflow = (el) => {
    const over = el.scrollWidth > el.clientWidth + 1;
    el.classList.toggle('is-overflow', over);
    el.classList.toggle('is-end', over && el.scrollLeft + el.clientWidth >= el.scrollWidth - 2);
  };
  const overflowObserver = 'ResizeObserver' in window
    ? new ResizeObserver((entries) => entries.forEach((entry) => updateOverflow(entry.target)))
    : null;
  Alpine.directive('overflow-fade', (el, _directive, { cleanup }) => {
    const onScroll = () => {
      if (overflowRows.get(el)) return;
      overflowRows.set(el, true);
      requestAnimationFrame(() => {
        overflowRows.set(el, false);
        updateOverflow(el);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    if (overflowObserver) overflowObserver.observe(el);
    updateOverflow(el);
    cleanup(() => {
      el.removeEventListener('scroll', onScroll);
      if (overflowObserver) overflowObserver.unobserve(el);
      overflowRows.delete(el);
    });
  });

  // --- FOLD (spec §33.2) ---
  // x-fold="{ open, from, lines, inert }" on a box that folds on phones. It measures the first
  // five lines of the box, counted from the element `from` matches (default: the box itself;
  // headings above it are not counted), and writes --zt-fold-a/-b/-c/-h, which
  // zatech-theme.css turns into the box's height and its graded fade. `lines`: a selector whose
  // matches each count as one line (table rows, a card's spec rows; matches on one row count
  // once); without it a line is `from`'s line-height. `inert`: parts made inert while they sit
  // below the fold. Content of five lines or fewer gets .zt-fold--short (no fade, no button).
  // `peek` (with `lines`): item mode, that many items in full and the next one fading (§36.1).
  // Desktop and open folds: no variables, nothing inert. One ResizeObserver serves all folds.
  const foldPhone = window.matchMedia('(max-width: 768px)');
  const foldUpdaters = new Map();
  const foldObserver = 'ResizeObserver' in window
    ? new ResizeObserver((entries) => entries.forEach((entry) => {
        const update = foldUpdaters.get(entry.target);
        if (update) update();
      }))
    : null;
  Alpine.directive('fold', (el, { expression }, { evaluateLater, effect, cleanup }) => {
    const getConfig = evaluateLater(expression || '{}');
    const vars = ['--zt-fold-a', '--zt-fold-b', '--zt-fold-c', '--zt-fold-h'];
    let config = {};
    let watched = [];
    const measure = () => {
      const parts = config.inert ? el.querySelectorAll(config.inert) : [];
      if (!foldPhone.matches || config.open) {
        vars.forEach((v) => el.style.removeProperty(v));
        el.classList.remove('zt-fold--short');
        parts.forEach((part) => { part.inert = false; });
        return;
      }
      const top = el.getBoundingClientRect().top;
      let from = (config.from && el.querySelector(config.from)) || el;
      // A start element that is not rendered (e.g. the first answer after its question was
      // closed) has no position: count from the box instead.
      if (!from.getClientRects().length) from = el;
      const start = from.getBoundingClientRect().top;
      const lines = [];
      if (config.lines) {
        el.querySelectorAll(config.lines).forEach((node) => {
          const r = node.getBoundingClientRect();
          if (r.height < 1 || r.top < start - 2) return;
          const same = lines.find((line) => Math.abs(line.top - r.top) < 6);
          if (same) same.bottom = Math.max(same.bottom, r.bottom);
          else lines.push({ top: r.top, bottom: r.bottom });
        });
        lines.sort((a, b) => a.top - b.top);
      } else {
        const lh = parseFloat(getComputedStyle(from).lineHeight) || 24;
        for (let i = 0; i < 5; i++) lines.push({ top: start + i * lh, bottom: start + (i + 1) * lh });
      }
      if (!lines.length) return;
      let marks;
      let short;
      if (config.peek && config.lines) {
        // Item mode (spec §36.1): `peek` items in full, the next one fading out, the rest hidden.
        // Fewer items than that plus two are shown whole (nothing worth folding).
        const fade = lines[Math.min(config.peek, lines.length - 1)];
        const span = fade.bottom - fade.top;
        marks = [fade.top - top, fade.top - top + span * 0.35, fade.top - top + span * 0.7, fade.bottom - top];
        short = lines.length < config.peek + 2;
      } else {
        while (lines.length < 5) lines.push(lines[lines.length - 1]);
        marks = [lines[2].top - top, lines[2].bottom - top, lines[3].bottom - top, lines[4].bottom - top];
        short = el.scrollHeight <= marks[3] + 8;
      }
      const height = marks[3];
      el.classList.toggle('zt-fold--short', short);
      if (short) {
        vars.forEach((v) => el.style.removeProperty(v));
        parts.forEach((part) => { part.inert = false; });
        return;
      }
      vars.forEach((v, i) => el.style.setProperty(v, Math.round(marks[i]) + 'px'));
      parts.forEach((part) => { part.inert = part.getBoundingClientRect().top - top >= height - 4; });
    };
    const watch = () => {
      if (!foldObserver) return;
      watched.forEach((node) => { foldObserver.unobserve(node); foldUpdaters.delete(node); });
      const from = (config.from && el.querySelector(config.from)) || el.firstElementChild;
      watched = [el, from].filter(Boolean);
      watched.forEach((node) => { foldUpdaters.set(node, measure); foldObserver.observe(node); });
    };
    // "Show less" (user, 2026-10-21): when a fold closes while its start has scrolled away, the
    // page returns to the section's start (just under the fixed and sticky bars) instead of
    // leaving the visitor further down the page by the height that was removed.
    // Pin + glide (user, 2026-10-22): the removed height used to pull the content below into view
    // before the page scrolled back. Now, in the frame the fold closes (before paint), the page is
    // shifted so the tapped control stays where it was, then glides the short rest to the start.
    // The control's position is read in the capture phase, before its own click handler closes.
    const control = el.nextElementSibling && el.nextElementSibling.matches('.zt-fold-more') ? el.nextElementSibling : null;
    let pinTop = null;
    const onControl = () => { pinTop = control.getBoundingClientRect().top; };
    if (control) control.addEventListener('click', onControl, true);
    const scrollBack = (pin) => {
      const anchor = el.parentElement || el;
      const bar = document.querySelector('.zt-jump-section');
      const announce = document.querySelector('.announcement-bar-clean');
      let offset = 12;
      if (bar && getComputedStyle(bar).position === 'sticky') offset += bar.getBoundingClientRect().bottom;
      else if (announce && getComputedStyle(announce).position === 'fixed') offset += announce.offsetHeight;
      let top = anchor.getBoundingClientRect().top;
      if (top >= offset) return;
      if (pin !== null) {
        const shift = control.getBoundingClientRect().top - pin;
        if (shift) {
          window.scrollTo({ top: Math.max(0, window.scrollY + shift), behavior: 'auto' });
          top = anchor.getBoundingClientRect().top;
          if (top >= offset) return;
        }
      }
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(0, window.scrollY + top - offset), behavior: reduce ? 'auto' : 'smooth' });
    };
    let wasOpen = false;
    effect(() => getConfig((value) => {
      config = value || {};
      watch();
      measure();
      const pin = pinTop;
      pinTop = null;
      if (wasOpen && !config.open && foldPhone.matches) requestAnimationFrame(() => scrollBack(pin));
      wasOpen = !!config.open;
    }));
    foldPhone.addEventListener('change', measure);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    cleanup(() => {
      if (control) control.removeEventListener('click', onControl, true);
      foldPhone.removeEventListener('change', measure);
      if (foldObserver) watched.forEach((node) => { foldObserver.unobserve(node); foldUpdaters.delete(node); });
    });
  });

  // --- COMPONENT DATA ---

  // Science Product Morphing Engine
  Alpine.data('scienceProduct', (initialData) => ({
    handle: initialData.handle,
    title: initialData.title,
    price: initialData.price,
    img: initialData.img,
    variantId: initialData.variantId,
    sizeIndex: initialData.sizeIndex || 0,
    moneyFormat: initialData.moneyFormat || '${{amount}}',
    loading: false,
    quantity: 1,
    variants: initialData.variants || [],
    hoverTitle: '',
    showSizeGuide: false,
    productCache: {}, // In-memory cache for product details
    ...quantityPendingMixin(),

    getInitial(title) {
      if (!title) return '';
      let t = title.split(' | ')[0];
      let lower = t.toLowerCase();
      if (lower.includes('xx-large') || lower.includes('xxl') || lower.includes('2xl')) return 'XXL';
      if (lower.includes('extra large') || lower.includes('x-large') || lower.includes('xl')) return 'XL';
      if (lower.includes('small')) return 'S';
      if (lower.includes('medium')) return 'M';
      if (lower.includes('large')) return 'L';
      return t.charAt(0).toUpperCase();
    },

    async switchProduct(newHandle) {
      if (newHandle === this.handle) return;
      this.clearPendingPromo();
      this.loading = true;
      try {
        let p;
        // Optimization A: Read variant JSON from memory cache if available
        if (this.productCache[newHandle]) {
          p = this.productCache[newHandle];
        } else {
          const response = await fetch(`/products/${newHandle}.js`);
          p = await response.json();
          this.productCache[newHandle] = p;
        }
        
        // Optimization B: Skip inventory network query if variant stocks are already in the global registry
        const hasInventory = p.variants.every(v => Alpine.store('inventorySync').warehouseStock[v.id] !== undefined);
        if (!hasInventory) {
          try {
            const invResponse = await fetch(`/products/${newHandle}?view=inventory`);
            if (invResponse.ok) {
              const invData = await invResponse.json();
              invData.variants.forEach(v => {
                Alpine.store('inventorySync').warehouseStock[v.id] = v.inventory;
              });
            }
          } catch (e) {
            console.warn('Inventory fetch failed for ' + newHandle, e);
          }
        }

        this.handle = newHandle;
        this.price = typeof Shopify !== 'undefined' && Shopify.formatMoney ? Shopify.formatMoney(p.price, this.moneyFormat) : '$' + (p.price / 100).toFixed(2);
        this.img = p.variants[0].featured_image ? p.variants[0].featured_image.src : p.images[0];
        this.title = p.title;
        this.variantId = p.variants[0].id;
        this.quantity = 1;
        this.variants = p.variants.map(v => {
          let rawSize = v.options && v.options[this.sizeIndex] ? v.options[this.sizeIndex] : v.title;
          return { id: v.id, title: rawSize, initial: this.getInitial(rawSize) };
        });
      } catch (e) { console.error('Product swap failed', e); }
      this.loading = false;
    },
    
    get trueAvailable() {
      return Alpine.store('inventorySync').getTrueAvailable(this.variantId);
    },

    async addToCart() {
      if (this.trueAvailable <= 0) return;
      this.loading = true;
      try {
        const added = await Alpine.store('cartModule').add(this.variantId, this.quantity, false, 'hub_page');
        if (added) {
          this.quantity = 1;
          this.clearPendingPromo();
        }
      } finally {
        this.loading = false;
      }
    }
  }));

  // Standard Product Grid Engine (Horizon Grid)
  Alpine.data('gridProduct', (initialData = {}, extraMethods = {}) => ({
    handle: initialData.handle || '',
    source: initialData.source || 'collection_grid', // 'hub_page' on science pages (spec §28)
    variantId: initialData.variantId || null,
    variants: initialData.variants || [],
    quantity: 1,
    loading: false,
    added: false,
    hoverTitle: '',
    ...quantityPendingMixin(),
    ...extraMethods,

    get trueAvailable() {
      return Alpine.store('inventorySync').getTrueAvailable(this.variantId);
    },

    init() {
      if (this.variantId) this.syncPendingPromo();
      this.$watch('variantId', () => {
        this.$nextTick(() => {
          if (this.variantId) {
            const maxAvailable = this.trueAvailable;
            if (this.quantity > maxAvailable) {
              this.quantity = Math.max(1, maxAvailable);
            }
          }
        });
      });
    },

    selectSize(id) {
      if (this.variantId === id) {
        this.variantId = null;
        this.clearPendingPromo();
      } else {
        this.variantId = id;
        this.added = false;
        this.syncPendingPromo();
      }
    },

    async addToCart() {
      if (!this.variantId || this.trueAvailable <= 0) return;
      this.loading = true;
      try {
        const added = await Alpine.store('cartModule').add(this.variantId, this.quantity, false, this.source);
        if (added) {
          this.resetSelection();
        }
      } catch (e) {
        console.error('[ATC Error]', e);
      } finally {
        this.loading = false;
      }
    }
  }));
});

// Judge.me Cards Carousel Color Synchronizer
// Judge.me injects its carousel asynchronously (timing varies), so a MutationObserver
// is the only reliable way to catch it appearing. It must disconnect once it has,
// otherwise it keeps re-running its querySelector on every DOM mutation on the page
// (cart drawer opens, quantity changes, swatch swaps, ...) for the entire session.
(function() {
  function captureFromCard(card) {
    const style = window.getComputedStyle(card);
    const bg = style.backgroundColor;
    const color = style.color;
    const hasBg = bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent';

    if (hasBg) {
      document.documentElement.style.setProperty('--jdgm-custom-bg', bg);
      try { localStorage.setItem('jdgm_custom_bg', bg); } catch (e) {}
    }
    if (color) {
      document.documentElement.style.setProperty('--jdgm-custom-color', color);
      try { localStorage.setItem('jdgm_custom_color', color); } catch (e) {}
    }
  }

  // Apply last-known colors immediately so there's no flash before the widget loads.
  try {
    const storedBg = localStorage.getItem('jdgm_custom_bg');
    const storedColor = localStorage.getItem('jdgm_custom_color');
    if (storedBg) document.documentElement.style.setProperty('--jdgm-custom-bg', storedBg);
    if (storedColor) document.documentElement.style.setProperty('--jdgm-custom-color', storedColor);
  } catch (e) {}

  const existingCard = document.querySelector('.jdgm-carousel-item');
  if (existingCard) {
    captureFromCard(existingCard);
    return; // Already present — nothing async to wait for.
  }

  const observer = new MutationObserver(() => {
    const card = document.querySelector('.jdgm-carousel-item');
    if (card) {
      captureFromCard(card);
      observer.disconnect();
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });

  // Ceiling for pages that never get a Judge.me carousel at all (most non-PDP/home pages).
  setTimeout(() => observer.disconnect(), 10000);
})();

